import { useEffect } from "react";
import { getThankYouPage } from "services/get-getThankYouPage";
import { cartState } from "atoms";
import { useRecoilState } from "recoil";
import { useWrapRequest } from "utils";
import { useNavigate } from "react-router-dom";
import { OPTION_SHIP, OPTION_STORE_PICKUP } from "utils/const";
import { formatPhoneNumber } from "utils";
import CalendarDownload from "components/ThankYou/CalendarDownload";
import { gtmPurchase } from "utils/googleTagManager";
import iconInternet from "images/internet-icon.png";
import iconTv from "images/tv-icon.png";
import iconSecurity from "images/security-icon.png";
import iconPhone from "images/phone-icon.png";
import "components/ThankYou/thank-you.scss";

interface Product {
  isNew: boolean;
  isUpgrade: boolean;
  categories: Category[];
  productName: string;
}

interface Category {
  cartServiceOptions: CartServiceOption[];
}

interface CartServiceOption {
  serviceOptionName: string;
  selectedQuantity: number;
  status: string;
  currentQuantity: number;
}

const ThankYou = () => {
  const [cart, setCart] = useRecoilState(cartState);
  const wrapRequest = useWrapRequest();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchThankyouPage = () => {
      wrapRequest(async () => {
        const getThankYouPageResponse = await getThankYouPage();
        const links = getThankYouPageResponse["_links"];
        const windowLocation = "ec:windowLocation";

        if (getThankYouPageResponse._embedded) {
          const cart = getThankYouPageResponse._embedded["ec:cart"];
          setCart(cart);
          gtmPurchase(cart);
        } else {
          navigate(links[windowLocation].href);
        }
      });
    };
    fetchThankyouPage();
    // eslint-disable-next-line
  }, [navigate, setCart]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const telephoneNumber = cart?.list?.cartProducts?.find((p) => p.assignedTelephoneNumber != null);
  const displayTelephoneNumber = telephoneNumber ? telephoneNumber.assignedTelephoneNumber : null;

  const serviceDisplayContainer = (index: number, icon: string, description: string) => {
    return (
      <div key={index} className="coretype-display">
        <img src={icon} alt="Shop Comporium" />
        <p>
          <strong>
            <span className="plus-sign">{description === "INTERNET" || description === "SECURITY" ? "" : "+"} </span>
            {description}
          </strong>
        </p>
      </div>
    );
  };

  const purchasedServices = (
    <div className="services-blurb">
      <strong>Your Service(s)</strong>
      <span>{cart?.bundle ? <div dangerouslySetInnerHTML={{ __html: cart.bundle.name }} /> : null}</span>
      {cart?.list?.cartProducts
        ? cart.list.cartProducts.map((product: Product, index: number) => {
            const productName = product.isNew || product.isUpgrade ? <div key={`product-${index}`}>{product.productName}</div> : null;
            const filterCartServiceOptions = product.categories.flatMap((product: Category) => product.cartServiceOptions);
            const selectedServiceOption = filterCartServiceOptions.map((option: CartServiceOption, index: number) => {
              if (option.selectedQuantity > option.currentQuantity && option.status === "Optional") {
                return <div key={`serviceOption-${index}`}>{option.serviceOptionName}</div>;
              } else {
                return null;
              }
            });
            return (
              <div key={index}>
                {productName}
                {selectedServiceOption}
              </div>
            );
          })
        : null}
    </div>
  );

  const registerForMyComporiumLink = (
    <div className="schedule-heading mt-2 mb-5">
      <button
        className="btn btn-secondary"
        style={{ backgroundColor: "#337ab7" }}
        onClick={() => window.open("https://my.comporium.com/register", "_blank")}
      >
        Register for My Comporium
      </button>
    </div>
  );

  const scheduleHeading = (
    <div className="schedule-heading mt-2 mb-5">
      <h2>Scheduled Installation</h2>
      <p className="schedule-dates">The date(s) you selected to have your service installed.</p>
    </div>
  );

  const shippingMessage =
    cart?.schedule?.selectedInstallType === OPTION_SHIP ? (
      <p className="ship-message">You will also receive an email when your equipment has been shipped.</p>
    ) : null;

  const accountNumber = cart?.billId ? <p className="account-number">Your account number is {cart.billId}.</p> : null;

  const storePickup =
    OPTION_STORE_PICKUP === cart?.schedule?.selectedInstallType ? (
      <div className="store-pickup w-50">
        Please stop by{" "}
        <a href="https://www.comporium.com/find-a-retail-location/" target="_blank" rel="noreferrer">
          your local Comporium store
        </a>{" "}
        to pick up the required equipment for your order. The account holder must be present in the store to pick up equipment.
      </div>
    ) : null;

  const formatScheduleInstallationDate = (date: string) => {
    let formatDate;
    let formatTime;
    if (date.includes("Between")) {
      // Split the string by "Between" to separate the date from the time range
      const parts = date.split(" Between ");
      // Extract the date part and the time range part
      formatDate = parts[0].trim(); // "May 09, 2024"
      formatTime = `Between ${parts[1].trim()}`; // "Between 1PM and 5PM"
    } else {
      formatDate = date.substring(0, 11);
      formatTime = date.replace(formatDate, "");
    }
    return (
      <div>
        <p>{formatDate}</p>
        <p>{formatTime}</p>
      </div>
    );
  };

  const scheduleWidget1 =
    cart?.schedule?.nonSecuritySchedule?.description && cart?.list?.cartProducts ? (
      <div className="service-installation-container-non-security">
        <div className="icon-display">
          {cart.list.cartProducts.map((service, index) => {
            if (service.serviceCoreTypeName === "HSI") {
              return serviceDisplayContainer(index, iconInternet, "INTERNET");
            } else if (service.serviceCoreTypeName === "TV") {
              return serviceDisplayContainer(index, iconTv, "TV");
            } else if (service.serviceCoreTypeName === "TEL") {
              return serviceDisplayContainer(index, iconPhone, "PHONE");
            } else {
              return null;
            }
          })}
        </div>
        <div>{formatScheduleInstallationDate(cart.schedule.nonSecuritySchedule.description)}</div>
      </div>
    ) : null;

  const scheduleWidget2 =
    cart?.schedule?.securitySchedule?.description && cart?.list?.cartProducts ? (
      <div className="service-installation-container-security">
        <div className="TYnon-security-display">
          {cart.list.cartProducts.map((service: { serviceCoreTypeName: string }, index: number) => {
            if (service.serviceCoreTypeName === "SEC") {
              return serviceDisplayContainer(index, iconSecurity, "SECURITY");
            } else {
              return null;
            }
          })}
        </div>
        <div>{formatScheduleInstallationDate(cart.schedule.securitySchedule.description)}</div>
      </div>
    ) : null;

  const scheduleRow = (
    <div className="row">
      {!cart.isCurrentCustomer ? registerForMyComporiumLink : null}
      {purchasedServices}
      <div className="col-12">
        <div className="TYsep2"></div>
        {scheduleWidget1 || scheduleWidget2 ? scheduleHeading : null}
        <div className="schedule-widget-container">
          {scheduleWidget1}
          {scheduleWidget2}
        </div>
      </div>
      {cart?.schedule?.nonSecuritySchedule?.startTime || cart?.schedule?.securitySchedule?.startTime ? (
        <div className="d-flex justify-content-center mt-5">
          <CalendarDownload cart={cart} />
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="thank-you-container">
      <div className="row">
        <div className="ThankYou">
          <div className="row">
            <div className="col-12">
              <h1 className="thank-you-header">Order Complete</h1>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <div className="TYsep1"></div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 thank-you-message">
              <div>Your order has been completed! An email confirmation will be sent shortly.</div>
              {shippingMessage}
              <div className="schedule-widget">
                {accountNumber}
                {displayTelephoneNumber ? (
                  <div className="display-phone-number">Your new telephone number is {formatPhoneNumber(displayTelephoneNumber)}</div>
                ) : null}
              </div>
            </div>
          </div>
          {scheduleRow}
          <div className="row">
            <div className="col-12 pick-up-store-message">{storePickup}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
